import React, { useState } from 'react';
import { classificationStandards } from '../../data/standardsData';

const ProductClassification = () => {
  const [selectedStandard, setSelectedStandard] = useState(classificationStandards[0]);
  
  // Handle standard change - in POC, only GPC is implemented
  const handleStandardChange = (standardId) => {
    const standard = classificationStandards.find(s => s.id === parseInt(standardId));
    
    // For POC, show that other standards aren't available
    if (standard.id !== 1) {
      alert(`${standard.name} (${standard.code}) standard is not available in this POC version. Only GPC is currently implemented.`);
      return;
    }
    
    setSelectedStandard(standard);
  };
  
  return (
    <div className="bg-white p-6 rounded-lg shadow-sm">
      {/* POC Warning Banner */}
      <div className="bg-amber-100 border-l-4 border-amber-500 p-4 mb-8">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-6 w-6 text-amber-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-lg font-medium text-amber-800">Proof of Concept Feature</h3>
            <div className="mt-2 text-amber-700">
              <p className="text-base">
                In this POC, only the GPC standard is fully implemented. Other classification standards are shown for 
                demonstration purposes but are not functional.
              </p>
            </div>
          </div>
        </div>
      </div>

      <h1 className="text-3xl font-bold text-gray-800 mb-6">Product Classification Standards</h1>
      
      <p className="text-lg text-gray-600 mb-8">
        Tiritix supports multiple product classification standards, allowing you to analyze product data using 
        your preferred taxonomy. Select a standard to view its structure and explore product data organized 
        according to that standard.
      </p>
      
      {/* Standard selector */}
      <div className="mb-8">
        <label className="block text-sm font-medium text-gray-600 mb-3">Classification Standard</label>
        <div className="flex">
          <select 
            value={selectedStandard.id}
            onChange={(e) => handleStandardChange(e.target.value)}
            className="dropdown-selector w-64"
          >
            {classificationStandards.map(standard => (
              <option 
                key={standard.id} 
                value={standard.id}
                disabled={!standard.isActive}
              >
                {standard.code} - {standard.name} {!standard.isActive && '(Not Available in POC)'}
              </option>
            ))}
          </select>
        </div>
      </div>
      
      {/* Standard details */}
      <div className="bg-blue-50 p-6 rounded-lg border border-blue-100 mb-8">
        <h2 className="text-xl font-bold text-blue-800 mb-2">{selectedStandard.name} ({selectedStandard.code})</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <p className="text-gray-700 mb-4">
              <span className="font-medium">Description:</span> {selectedStandard.description}
            </p>
            <p className="text-gray-700 mb-4">
              <span className="font-medium">Current Version:</span> {selectedStandard.version}
            </p>
            <p className="text-gray-700">
              <span className="font-medium">Status:</span> {selectedStandard.isActive ? (
                <span className="text-green-600">Active</span>
              ) : (
                <span className="text-gray-500">Not Available in POC</span>
              )}
            </p>
          </div>
          <div>
            <p className="text-gray-700 mb-2 font-medium">Key Features:</p>
            <ul className="list-disc list-inside space-y-1 text-gray-700">
              <li>Hierarchical 3-level classification structure</li>
              <li>Globally standardized taxonomy</li>
              <li>Cross-category comparability</li>
              <li>Regular updates to reflect market changes</li>
            </ul>
          </div>
        </div>
      </div>
      
      {/* Classification structure visualization */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Classification Structure</h2>
        
        {selectedStandard.code === 'GPC' ? (
          <div className="border border-gray-200 rounded-lg p-4 bg-white">
            <h3 className="text-lg font-medium text-gray-800 mb-4">GPC Hierarchy Treeview</h3>
            
            {/* Simplified treeview visualization for GPC */}
            <div className="overflow-auto">
              <div className="pl-4 border-l-2 border-blue-300">
                <div className="mb-4">
                  <div className="font-bold text-blue-800 mb-2">Segment: Electronics</div>
                  <div className="pl-4 border-l-2 border-green-300">
                    <div className="mb-3">
                      <div className="font-medium text-green-800 mb-1">Family: Computing Devices</div>
                      <div className="pl-4 border-l-2 border-gray-300">
                        <div className="text-gray-600">Class: Laptops</div>
                        <div className="text-gray-600">Class: Desktops</div>
                        <div className="text-gray-600">Class: Tablets</div>
                      </div>
                    </div>
                    <div className="mb-3">
                      <div className="font-medium text-green-800 mb-1">Family: Mobile Communications</div>
                      <div className="pl-4 border-l-2 border-gray-300">
                        <div className="text-gray-600">Class: Smartphones</div>
                        <div className="text-gray-600">Class: Feature Phones</div>
                        <div className="text-gray-600">Class: Accessories</div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mb-4">
                  <div className="font-bold text-blue-800 mb-2">Segment: Clothing</div>
                  <div className="pl-4 border-l-2 border-green-300">
                    <div className="mb-3">
                      <div className="font-medium text-green-800 mb-1">Family: Outerwear</div>
                      <div className="pl-4 border-l-2 border-gray-300">
                        <div className="text-gray-600">Class: Jackets</div>
                        <div className="text-gray-600">Class: Coats</div>
                        <div className="text-gray-600">Class: Vests</div>
                      </div>
                    </div>
                    <div className="mb-3">
                      <div className="font-medium text-green-800 mb-1">Family: Footwear</div>
                      <div className="pl-4 border-l-2 border-gray-300">
                        <div className="text-gray-600">Class: Casual Shoes</div>
                        <div className="text-gray-600">Class: Athletic Shoes</div>
                        <div className="text-gray-600">Class: Boots</div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mb-4">
                  <div className="font-bold text-blue-800 mb-2">Segment: Food & Beverage</div>
                  <div className="pl-4 border-l-2 border-green-300">
                    <div className="mb-3">
                      <div className="font-medium text-green-800 mb-1">Family: Beverages</div>
                      <div className="pl-4 border-l-2 border-gray-300">
                        <div className="text-gray-600">Class: Soft Drinks</div>
                        <div className="text-gray-600">Class: Alcoholic Drinks</div>
                        <div className="text-gray-600">Class: Coffee & Tea</div>
                      </div>
                    </div>
                    <div className="mb-3">
                      <div className="font-medium text-green-800 mb-1">Family: Prepared Foods</div>
                      <div className="pl-4 border-l-2 border-gray-300">
                        <div className="text-gray-600">Class: Ready Meals</div>
                        <div className="text-gray-600">Class: Frozen Foods</div>
                        <div className="text-gray-600">Class: Canned Foods</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <p className="text-sm text-gray-500 mt-4">
              This is a simplified representation of the GPC hierarchy structure. The actual GPC standard contains
              thousands of categories across multiple segments, families, classes, and bricks.
            </p>
          </div>
        ) : (
          <div className="bg-gray-100 p-12 rounded-lg border border-gray-200 text-center">
            <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-xl font-medium text-gray-700 mb-2">Standard Not Available in POC</h3>
            <p className="text-gray-600">
              The {selectedStandard.name} ({selectedStandard.code}) standard is not implemented in this proof of concept.
              Please select GPC to view the classification structure.
            </p>
          </div>
        )}
      </div>
      
      <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-3">Classification Standards Comparison</h2>
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Standard</th>
                <th>Levels</th>
                <th>Focus Area</th>
                <th>Global Acceptance</th>
                <th>Status in POC</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>GPC</td>
                <td>4</td>
                <td>Retail Consumer Products</td>
                <td>High</td>
                <td>Implemented</td>
              </tr>
              <tr>
                <td>UNSPSC</td>
                <td>4</td>
                <td>Products & Services</td>
                <td>Medium</td>
                <td>Not Available</td>
              </tr>
              <tr>
                <td>TIRITIX</td>
                <td>3</td>
                <td>Customized Analytics</td>
                <td>Proprietary</td>
                <td>Not Available</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ProductClassification;
